import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getNestedValue } from '../utils/get-nested-value'

export const dataTableDefaultValues: Omit<DataTableItem, 'id'> = {
    version: 1,
    resultsCount: 0,
    totalResultCount: 0,
    totalFilteredOnlyResultCount: 0,
    selectedRows: [],
    hiddenColumns: [],
    showHiddenColumnsModal: false,
    params: {
        page: 0,
        perPage: 10,
        search: '',
        sortBy: [],
        filters: []
    }
}

interface FilterItem<T> {
    column: T
    operator: 'IN' | 'NOT IN'
    values: string[]
}

/**
 * These values will be passed on to the HTTP requests
 */
export interface DataTableItemParams<T = string> {
    search: string
    page: number
    perPage: number
    sortBy: { column: string; order: 'asc' | 'desc' }[]
    filters: FilterItem<T>[]
}

export interface DataTableItem {
    id: string
    version: number
    resultsCount: number
    totalResultCount: number
    totalFilteredOnlyResultCount: number
    selectedRows: string[]
    hiddenColumns: string[]
    showHiddenColumnsModal: boolean
    params: DataTableItemParams
}

type UpdateDataTableValues = Omit<Partial<DataTableItem>, 'id' | 'params'> & {
    params?: Partial<DataTableItemParams>
}

type CreateDataTableValues = Omit<Partial<DataTableItem>, 'id' | 'params'> &
    Pick<DataTableItem, 'id'> & { params: Partial<DataTableItemParams> }

const dataTableSlice = createSlice({
    name: 'data-tables',
    initialState: [] as DataTableItem[],
    reducers: {
        createDataTable(state, action: PayloadAction<CreateDataTableValues>) {
            clearExpiredItemsFromLocalStorage()
            const storedItem = getItemFromLocalStorage(
                action.payload.id,
                action.payload.version
            )

            const newItem = {
                id: action.payload.id,
                version:
                    action.payload.version ?? dataTableDefaultValues.version,
                params: {
                    page:
                        action.payload.params?.page ??
                        dataTableDefaultValues.params.page,
                    perPage:
                        getStoredValueItem(storedItem, 'params.perPage') ??
                        action.payload.params?.perPage ??
                        dataTableDefaultValues.params.perPage,
                    search:
                        action.payload.params?.search ??
                        dataTableDefaultValues.params.search,
                    // TODO: We'll comment it until the feature of sort is ready
                    // sortBy: getStoredValueItem(storedItem, 'params.sortBy') ?? action.payload.params?.sortBy ?? dataTableDefaultValues.params.sortBy,
                    sortBy:
                        action.payload.params?.sortBy ??
                        dataTableDefaultValues.params.sortBy,
                    filters:
                        action.payload.params?.filters ??
                        dataTableDefaultValues.params.filters
                },
                selectedRows: dataTableDefaultValues.selectedRows,
                hiddenColumns:
                    getStoredValueItem(storedItem, 'hiddenColumns') ??
                    dataTableDefaultValues.hiddenColumns,
                showHiddenColumnsModal:
                    dataTableDefaultValues.showHiddenColumnsModal,
                resultsCount:
                    action.payload.resultsCount ??
                    dataTableDefaultValues.resultsCount,
                totalResultCount:
                    action.payload.totalResultCount ??
                    dataTableDefaultValues.totalResultCount,
                totalFilteredOnlyResultCount:
                    action.payload.totalFilteredOnlyResultCount ??
                    dataTableDefaultValues.totalFilteredOnlyResultCount
            }

            state.push(newItem)
            storeItemOnLocalStorage(action.payload.id, newItem)
        },

        updateDataTable(
            state,
            action: PayloadAction<{ id: string; values: UpdateDataTableValues }>
        ) {
            const { id, values } = action.payload
            const { params: paramValues, ...tableValues } = values

            return state.map((dataTable) => {
                if (dataTable.id === id) {
                    const newValue = {
                        ...dataTable,
                        ...tableValues,
                        params: {
                            ...dataTable.params,
                            ...paramValues
                        }
                    }

                    storeItemOnLocalStorage(id, newValue)
                    return newValue
                }

                return dataTable
            })
        },

        updateAllDataTable(
            state,
            action: PayloadAction<{ values: UpdateDataTableValues }>
        ) {
            const { values } = action.payload
            const { params: paramValues, ...tableValues } = values

            return state.map((dataTable) => {
                return {
                    ...dataTable,
                    ...tableValues,
                    params: {
                        ...dataTable.params,
                        ...paramValues
                    }
                }
            })
        },

        removeDataTable(state, action: PayloadAction<{ id: string }>) {
            const tableId = action.payload.id

            return state.filter((state) => state.id !== tableId)
        }
    }
})

export const {
    createDataTable,
    updateDataTable,
    updateAllDataTable,
    removeDataTable
} = dataTableSlice.actions

export default dataTableSlice.reducer

const dataTablePrefix = 'DataTable.'
const oneWeek = 1000 * 60 * 60 * 24 * 7

function clearExpiredItemsFromLocalStorage() {
    const itemsToRemove = Object.entries(localStorage)
        .filter((value) => {
            return value[0].startsWith(dataTablePrefix)
        })
        .map((item) => {
            return item[0]
        })

    itemsToRemove.forEach((item) => {
        const { expiry } = JSON.parse(localStorage.getItem(item) || '{}')

        if (!expiry || new Date().getTime() > expiry) {
            localStorage.removeItem(item)
        }
    })
}

export function clearItemsFromLocalStorage() {
    const itemsToRemove = Object.entries(localStorage)
        .filter((value) => {
            return value[0].startsWith(dataTablePrefix)
        })
        .map((item) => {
            return item[0]
        })

    itemsToRemove.forEach((item) => {
        localStorage.removeItem(item)
    })
}

function getItemFromLocalStorage(
    id: DataTableItem['id'],
    version: DataTableItem['version']
): object {
    const item = localStorage.getItem(`${dataTablePrefix}${id}:${version}`)

    if (!item) {
        return {}
    }

    const { expiry, value } = JSON.parse(item)

    if (!expiry || new Date().getTime() > expiry) {
        localStorage.removeItem(`${dataTablePrefix}${id}:${version}`)

        return {}
    }

    const object = {
        value,
        expiry: new Date().getTime() + oneWeek
    }

    localStorage.setItem(
        `${dataTablePrefix}${id}:${version}`,
        JSON.stringify(object)
    )

    return value
}

function storeItemOnLocalStorage(id: DataTableItem['id'], item: DataTableItem) {
    const version = item.version

    const values = {
        params: {
            sortBy: item.params.sortBy,
            perPage: item.params.perPage
        },
        hiddenColumns: item.hiddenColumns
    }

    const object = {
        value: values,
        expiry: new Date().getTime() + oneWeek
    }

    localStorage.setItem(
        `${dataTablePrefix}${id}:${version}`,
        JSON.stringify(object)
    )
}

function getStoredValueItem(storedValue: object, key: string): any {
    return getNestedValue(storedValue, key)
}
